<template>
  <div class="stripe-terminal-panel form-block mb-4">
    <h5 class="tw-mb-2">
      {{ $t("gateways.terminal_title", "Card readers") }}
    </h5>

    <div v-if="loading" class="tw-py-4 tw-text-center">
      <b-spinner small />
    </div>

    <template v-else>
      <error v-if="errors.length" :errors="errors" />

      <!-- Locations -->
      <div class="stripe-terminal-panel__section">
        <h6 class="tw-mb-2">{{ $t("gateways.terminal_locations_title", "Venues") }}</h6>

        <p class="tw-text-sm tw-text-[#6f7a8a] tw-mb-3">
          {{
            $t(
              "gateways.terminal_locations_help",
              "Every card reader belongs to a venue. Register the address where you will be taking payments; one venue is enough unless you run events in more than one place."
            )
          }}
        </p>

        <b-alert v-if="settings.locations_error" show variant="warning" class="tw-text-sm">
          {{
            $t(
              "gateways.terminal_locations_unavailable",
              "The venues could not be loaded from Stripe just now. The device key below is unaffected."
            )
          }}
        </b-alert>

        <b-alert
          v-else-if="!settings.locations.length && !showLocationForm"
          show
          variant="warning"
          class="tw-text-sm"
        >
          {{
            $t(
              "gateways.terminal_no_location",
              "No venue registered yet. Card readers cannot connect until there is one."
            )
          }}
        </b-alert>

        <!-- A div may wrap each dt/dd pair, which keeps one keyed element per
             venue: Vue 2 does not take a key on <template v-for>. -->
        <dl v-if="settings.locations.length" class="tw-mb-3">
          <div v-for="loc in settings.locations" :key="loc.id" class="row">
            <dt class="col-5 col-lg-4">{{ loc.display_name || loc.id }}</dt>
            <dd class="col-7 col-lg-8">{{ formatAddress(loc) }}</dd>
          </div>
        </dl>

        <form v-if="showLocationForm" class="row" @submit.prevent="registerLocation">
          <div class="col-12 col-lg-6">
            <div class="form-group">
              <label class="form-group__label form-group__label--required">
                {{ $t("gateways.terminal_location_name", "Venue name") }}
              </label>
              <input
                v-model.trim="locationForm.display_name"
                type="text"
                class="form-control"
                required
                maxlength="255"
              />
            </div>
          </div>
          <div class="col-12 col-lg-6">
            <div class="form-group">
              <label class="form-group__label form-group__label--required">
                {{ $t("gateways.terminal_location_line1", "Street address") }}
              </label>
              <input
                v-model.trim="locationForm.line1"
                type="text"
                class="form-control"
                required
                maxlength="255"
              />
            </div>
          </div>
          <div class="col-12 col-lg-6">
            <div class="form-group">
              <label class="form-group__label form-group__label--required">
                {{ $t("gateways.terminal_location_city", "City") }}
              </label>
              <input
                v-model.trim="locationForm.city"
                type="text"
                class="form-control"
                required
                maxlength="128"
              />
            </div>
          </div>
          <div class="col-12 col-lg-6">
            <div class="form-group">
              <label class="form-group__label">
                {{ $t("gateways.terminal_location_state", "State / region") }}
              </label>
              <input
                v-model.trim="locationForm.state"
                type="text"
                class="form-control"
                maxlength="128"
              />
            </div>
          </div>
          <div class="col-12 col-lg-6">
            <div class="form-group">
              <label class="form-group__label form-group__label--required">
                {{ $t("gateways.terminal_location_postal_code", "Postal code") }}
              </label>
              <input
                v-model.trim="locationForm.postal_code"
                type="text"
                class="form-control"
                required
                maxlength="32"
              />
            </div>
          </div>
          <div class="col-12 col-lg-6">
            <div class="form-group">
              <label class="form-group__label form-group__label--required">
                {{ $t("gateways.terminal_location_country", "Country") }}
              </label>
              <input
                v-model.trim="locationForm.country"
                type="text"
                class="form-control"
                required
                maxlength="2"
                placeholder="US"
                @input="locationForm.country = locationForm.country.toUpperCase().slice(0, 2)"
              />
              <small class="tw-text-xs tw-text-[#6f7a8a]">
                {{
                  $t("gateways.terminal_location_country_hint", "Two-letter code, e.g. US or GB")
                }}
              </small>
            </div>
          </div>

          <div class="col-12 tw-flex tw-gap-2">
            <button type="submit" class="bttn bttn--sm bttn--orange" :disabled="sending">
              {{ $t("gateways.terminal_location_save", "Register venue") }}
            </button>
            <button
              v-if="settings.locations.length"
              type="button"
              class="bttn bttn--sm bttn--grey"
              :disabled="sending"
              @click="showLocationForm = false"
            >
              {{ $t("common.cancel", "Cancel") }}
            </button>
          </div>
        </form>

        <button
          v-else
          type="button"
          class="bttn bttn--sm bttn--blue"
          :disabled="sending"
          @click="showLocationForm = true"
        >
          {{ $t("gateways.terminal_location_add", "Add a venue") }}
        </button>
      </div>

      <!-- Device key -->
      <div class="stripe-terminal-panel__section tw-mt-4 tw-pt-3">
        <h6 class="tw-mb-2">{{ $t("gateways.terminal_key_title", "Device key") }}</h6>

        <p class="tw-text-sm tw-text-[#6f7a8a] tw-mb-3">
          {{
            $t(
              "gateways.terminal_key_help",
              "Your card reader screen needs this key to connect. Open the link below on the device that will be taking payments. Treat it like a password: anyone with the link can connect a reader to your account."
            )
          }}
        </p>

        <template v-if="settings.terminal_key">
          <div v-if="deviceLinks.length" class="tw-mb-3">
            <div
              v-for="link in deviceLinks"
              :key="link.cid"
              class="stripe-terminal-panel__link tw-mb-2"
            >
              <div class="tw-text-sm tw-mb-1">
                {{ $t("gateways.terminal_key_link_for", "Campaign") }} {{ link.cid }}
              </div>
              <div class="tw-flex tw-gap-2 tw-items-center">
                <input :value="link.url" type="text" class="form-control" readonly />
                <button
                  type="button"
                  class="bttn bttn--sm bttn--blue tw-whitespace-nowrap"
                  @click="copy(link.url)"
                >
                  {{
                    copied === link.url
                      ? $t("gateways.terminal_key_copied", "Copied")
                      : $t("gateways.terminal_key_copy", "Copy")
                  }}
                </button>
              </div>
            </div>
          </div>

          <!-- No campaign is attached to this gateway, so no link can be built;
               the key is still what the device needs. -->
          <div v-else class="tw-mb-3">
            <div class="tw-text-sm tw-mb-1">
              {{ $t("gateways.terminal_key_label", "Key") }}
            </div>
            <div class="tw-flex tw-gap-2 tw-items-center">
              <input :value="settings.terminal_key" type="text" class="form-control" readonly />
              <button
                type="button"
                class="bttn bttn--sm bttn--blue tw-whitespace-nowrap"
                @click="copy(settings.terminal_key)"
              >
                {{
                  copied === settings.terminal_key
                    ? $t("gateways.terminal_key_copied", "Copied")
                    : $t("gateways.terminal_key_copy", "Copy")
                }}
              </button>
            </div>
            <small class="tw-text-xs tw-text-[#6f7a8a]">
              {{
                $t(
                  "gateways.terminal_key_no_campaign",
                  "Attach a campaign to this payment option to get a ready-made device link."
                )
              }}
            </small>
          </div>

          <button
            type="button"
            class="bttn bttn--sm bttn--grey"
            :disabled="sending"
            @click="issueKey(true)"
          >
            {{ $t("gateways.terminal_key_regenerate", "Replace key") }}
          </button>
          <small class="tw-block tw-text-xs tw-text-[#6f7a8a] tw-mt-1">
            {{
              $t(
                "gateways.terminal_key_regenerate_hint",
                "Replacing the key stops every device still using the old link until you give them the new one."
              )
            }}
          </small>
        </template>

        <button
          v-else
          type="button"
          class="bttn bttn--sm bttn--orange"
          :disabled="sending"
          @click="issueKey(false)"
        >
          {{ $t("gateways.terminal_key_issue", "Create a device key") }}
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import axios from "axios";
import urls, { donateHost } from "@/constants/urls.js";
import error from "@/components/errors.vue";

const emptyLocationForm = () => ({
  display_name: "",
  line1: "",
  city: "",
  state: "",
  postal_code: "",
  country: "",
});

export default {
  name: "StripeTerminalPanel",

  components: { error },

  props: {
    // organization_merchant_info id.
    gatewayId: { type: [String, Number], required: true },
    // Campaign ids this payment option serves, used to build the device link.
    campaignIds: { type: Array, default: () => [] },
  },

  data() {
    return {
      loading: true,
      sending: false,
      errors: [],
      settings: { locations: [], terminal_key: "", locations_error: "" },
      showLocationForm: false,
      locationForm: emptyLocationForm(),
      copied: "",
    };
  },

  computed: {
    orgId() {
      return localStorage.getItem("orgId");
    },

    settingsUrl() {
      return urls.stripeTerminalSettings
        .replace(":oid", this.orgId)
        .replace(":idGateway", this.gatewayId);
    },

    // The device opens the donation form with the key in the query string, and
    // the form sends it on when it asks for a connection token.
    deviceLinks() {
      if (!this.settings.terminal_key) {
        return [];
      }

      return this.campaignIds.map((cid) => ({
        cid,
        url: `${donateHost}/${cid}?terminal_key=${encodeURIComponent(this.settings.terminal_key)}`,
      }));
    },
  },

  created() {
    this.fetch();
  },

  methods: {
    formatAddress(loc) {
      return [loc.line1, loc.city, loc.state, loc.postal_code, loc.country]
        .filter(Boolean)
        .join(", ");
    },

    fetch() {
      this.loading = true;
      axios
        .get(this.settingsUrl)
        .then(({ data }) => {
          this.settings = {
            locations: data.locations || [],
            terminal_key: data.terminal_key || "",
            locations_error: data.locations_error || "",
          };
          this.showLocationForm = this.settings.locations.length === 0;
        })
        .catch(this.showError)
        .finally(() => {
          this.loading = false;
        });
    },

    registerLocation() {
      this.errors = [];
      this.sending = true;
      axios
        .post(
          urls.stripeTerminalLocation
            .replace(":oid", this.orgId)
            .replace(":idGateway", this.gatewayId),
          this.locationForm
        )
        .then(() => {
          this.locationForm = emptyLocationForm();
          this.showLocationForm = false;
          this.fetch();
        })
        .catch(this.showError)
        .finally(() => {
          this.sending = false;
        });
    },

    issueKey(replacing) {
      if (
        replacing &&
        !window.confirm(
          this.$t(
            "gateways.terminal_key_regenerate_confirm",
            "Replace the device key? Readers still using the old link will stop working until you give them the new one."
          )
        )
      ) {
        return;
      }

      this.errors = [];
      this.sending = true;
      axios
        .post(
          urls.stripeTerminalKey.replace(":oid", this.orgId).replace(":idGateway", this.gatewayId)
        )
        .then(({ data }) => {
          this.settings = { ...this.settings, terminal_key: data.terminal_key };
        })
        .catch(this.showError)
        .finally(() => {
          this.sending = false;
        });
    },

    copy(text) {
      const dummy = document.createElement("textarea");
      document.body.appendChild(dummy);
      dummy.value = text;
      dummy.select();
      dummy.setSelectionRange(0, 99999);
      navigator.clipboard.writeText(dummy.value);
      document.body.removeChild(dummy);
      this.copied = text;
    },

    showError(e) {
      if (!e || !e.response) {
        this.errors.push(this.$t("template.error_default", "Something went wrong"));
        return;
      }
      const d = e.response.data;
      if (d && d.error) {
        this.errors.push(d.error);
      } else if (d && Array.isArray(d.errors)) {
        this.errors = d.errors.map((err) => err.detail || err.title).filter(Boolean);
      } else {
        this.errors.push(this.$t("template.error_default", "Something went wrong"));
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.stripe-terminal-panel {
  dt {
    font-family: $vito-bold;
  }

  &__section + &__section {
    border-top: 1px solid lightgrey;
  }
}
</style>
